import React, { useState } from "react";
import { Calendar, Info, Users } from "lucide-react";
import { toast } from "react-toastify";
import ConferenceTradeMissionVenueInfoSection from "./ConferenceTradeMissionVenueInfoSection";
import ThumbnailImageUpload from "./ThumbnailImageUpload";
import FeaturedImagesUpload from "./FeaturedImagesUpload";
import AboutEventSection from "./AboutEventSection";
import PerformersEntertainersSection from "./PerformersEntertainersSection";
import SponsorsSection from "./SponsorsSection";
import EventItinerarySection from "./EventItinerarySection";
import MotivationalQuestionsSection from "./MotivationalQuestionsSection";
import TotalParticipantSlotsSection from "./TotalParticipantSlotsSection";

/**
 * Step Three: Information Section Component
 * Handles all information for Conference/Trade Mission (without event types)
 */
const StepThreeInformationSection = ({
  formData,
  onUpdate,
  onBack,
  onNext,
  eventType, // "conference" or "trade_mission"
}) => {
  const handleSectionUpdate = (sectionData) => {
    onUpdate({
      ...formData,
      ...sectionData,
    });
  };

  const handleNext = () => {
    // Validation
    if (!formData.eventName || formData.eventName.trim() === "") {
      toast.error("Event title is required");
      return;
    }
    if (!formData.eventStartDate) {
      toast.error("Start date is required");
      return;
    }
    if (!formData.eventEndDate) {
      toast.error("End date is required");
      return;
    }
    if (formData.eventStartDate && formData.eventEndDate && formData.eventEndDate < formData.eventStartDate) {
      toast.error("End date must be on or after start date");
      return;
    }
    if (!formData.eventStartTime) {
      toast.error("Start time is required");
      return;
    }
    if (!formData.eventEndTime) {
      toast.error("End time is required");
      return;
    }
    if (!formData.venueName) {
      toast.error("Venue name is required");
      return;
    }
    if (!formData.venueStreetAddress) {
      toast.error("Venue street address is required");
      return;
    }
    if (!formData.country) {
      toast.error("Country is required");
      return;
    }
    if (!formData.city) {
      toast.error("City/State/Region is required");
      return;
    }
    if (!formData.thumbnail) {
      toast.error("Thumbnail image is required");
      return;
    }

    // Trade Mission specific validation
    if (eventType === "trade_mission") {
      if (!formData.totalParticipantSlots || parseInt(formData.totalParticipantSlots) <= 0) {
        toast.error("Total participant slots is required for trade missions");
        return;
      }
    }

    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
              {eventType === "conference" ? "Conference" : "Trade Mission"} Information
            </h2>
            <p className="mt-0.5 text-xs text-gray-600 sm:text-sm">
              Provide details about your {eventType === "conference" ? "conference" : "trade mission"} and venue
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="p-3 mt-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-start space-x-2">
            <Info className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-purple-800">
              <p className="mb-0.5 font-semibold">💡 Quick Tips:</p>
              <ul className="space-y-0.5 text-xs list-disc list-inside">
                <li>Ensure all venue information is accurate</li>
                <li>Google location helps attendees find the venue easily</li>
                <li>All fields marked with * are required</li>
                {eventType === "conference" && (
                  <li>Add motivational questions to understand attendee expectations</li>
                )}
                {eventType === "trade_mission" && (
                  <li>Set the total number of participant slots available</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Venue Information Section */}
      <ConferenceTradeMissionVenueInfoSection
        data={formData}
        onUpdate={handleSectionUpdate}
      />

      {/* Trade Mission: Total Participant Slots */}
      {eventType === "trade_mission" && (
        <TotalParticipantSlotsSection
          value={formData.totalParticipantSlots || ""}
          onChange={(value) => handleSectionUpdate({ totalParticipantSlots: value })}
        />
      )}

      {/* Thumbnail Image Upload */}
      <ThumbnailImageUpload
        value={formData.thumbnail}
        onChange={(value) => handleSectionUpdate({ thumbnail: value })}
        maxSize={5}
        acceptedFormats={["image/jpeg", "image/jpg", "image/png", "image/webp"]}
      />

      {/* Featured Images */}
      <FeaturedImagesUpload
        value={formData.featuredImages || []}
        onChange={(value) => handleSectionUpdate({ featuredImages: value })}
        maxSize={5}
        acceptedFormats={["image/jpeg", "image/jpg", "image/png", "image/webp"]}
      />

      {/* About Event Section */}
      <AboutEventSection
        value={formData.aboutEvent || ""}
        onChange={(value) => handleSectionUpdate({ aboutEvent: value })}
      />

      {/* Performers/Entertainers Section */}
      <PerformersEntertainersSection
        value={formData.performers || []}
        onChange={(value) => handleSectionUpdate({ performers: value })}
      />

      {/* Sponsors Section */}
      <SponsorsSection
        value={formData.sponsors || []}
        onChange={(value) => handleSectionUpdate({ sponsors: value })}
        maxSize={2}
        acceptedFormats={["image/jpeg", "image/jpg", "image/png", "image/webp"]}
      />

      {/* Conference: Motivational Questions */}
      {eventType === "conference" && (
        <MotivationalQuestionsSection
          value={formData.motivationalQuestions || []}
          onChange={(value) => handleSectionUpdate({ motivationalQuestions: value })}
        />
      )}

      {/* Event Itinerary */}
      <EventItinerarySection
        value={formData.itinerary || []}
        onChange={(value) => handleSectionUpdate({ itinerary: value })}
      />

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          onClick={onBack}
          className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg shadow-sm transition-all hover:shadow-md"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default StepThreeInformationSection;

